/*
 * RBN Live-Personalisierung (Name + Wunschtext) für Redblacknation
 * - Live-Vorschau auf dem Rückseitenbild
 * - Pro Set eigene Texte (Kaching Bundles: Ereignis "variants-changed")
 * - Beim Hinzufügen zum Warenkorb: jede personalisierte Einheit wird eine eigene Warenkorbzeile (Menge 1)
 *   mit den Properties "Name" / "Wunschtext". Alle übrigen Properties (z. B. __kaching_bundles) bleiben erhalten.
 * Gehört zu blocks/rbn-personalisierung.liquid und snippets/rbn-cart-hook.liquid.
 */

// Bezeichnungen in Warenkorb/Bestellung – werden aus den Block-Einstellungen gesetzt (prop_name / prop_text)
let PROP_NAME = 'Name';
let PROP_TEXT = 'Custom text';

// Englische Ersatztexte, falls eine Meldung in den Block-Einstellungen leer ist
const DEFAULT_MESSAGES = {
  max: 'Maximum {max} characters.',
  placeholder: '“{value}” is just a placeholder. Please enter your own text or leave the field empty.',
  chars: "These characters can't be printed: {chars}",
  tooLong: 'Too long for the print area. Please shorten it (fits about {approx} characters).',
  copied: 'Texts applied to all {count} sets. You can still edit each set individually.',
  cartMissing: "Attention: your personalization wasn't saved in the cart. Please check your cart or contact us before you order.",
  personalized: 'personalized',
};
let FONT = "'Crimson Text'"; // wird aus der Block-Einstellung (font_picker) gesetzt
const KACHING_TAG = 'kaching-bundles-block';
// Lateinische Schrift (inkl. ë, ç, ä, ö, ü, ß, č, š …), Ziffern, Leerzeichen und gängige Satzzeichen
const ALLOWED_CHAR = /[\p{Script=Latin}\p{M}\p{N} .,'’‘"“”„«»!?&:;()\/+#@*–—-]/u;
const LOG = '[RBN Personalisierung]';

/* ------------------------------------------------------------------ */
/* Hilfsfunktionen                                                     */
/* ------------------------------------------------------------------ */

const toId = (value) => String(value ?? '').replace(/\D/g, '');

function cleanValue(value, uppercase) {
  let v = String(value ?? '')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (uppercase) v = v.toUpperCase();
  return v;
}

function normalizeForCompare(value) {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

let measureCtx = null;
function measureWidth(text, fontPx, trackingEm) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = `400 ${fontPx}px ${FONT}, Georgia, serif`;
  const chars = Array.from(text).length;
  return measureCtx.measureText(text).width + trackingEm * fontPx * chars;
}

/* ---------- Request-Bodies lesen/schreiben (FormData, URL-encoded, JSON) ---------- */

function splitKey(key) {
  const head = key.match(/^[^[]+/);
  if (!head) return [key];
  const parts = [head[0]];
  const re = /\[([^\]]*)\]/g;
  let m;
  while ((m = re.exec(key))) parts.push(m[1]);
  return parts;
}

function setDeep(target, parts, value) {
  let node = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (node[key] == null || typeof node[key] !== 'object') node[key] = /^\d*$/.test(parts[i + 1]) ? [] : {};
    node = node[key];
  }
  const last = parts[parts.length - 1];
  if (Array.isArray(node) && last === '') node.push(value);
  else node[last] = value;
}

function entriesToObject(entries) {
  const obj = {};
  const sections = [];
  for (const [key, value] of entries) {
    if (typeof value !== 'string') continue; // Datei-Uploads o. Ä. ignorieren
    if (key === 'sections') {
      value.split(',').forEach((s) => s && !sections.includes(s) && sections.push(s));
      continue;
    }
    setDeep(obj, splitKey(key), value);
  }
  if (sections.length) obj.sections = sections.join(',');
  return obj;
}

function flatten(value, prefix, out) {
  if (value == null) return out;
  if (Array.isArray(value)) {
    value.forEach((v, i) => flatten(v, `${prefix}[${i}]`, out));
  } else if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) flatten(v, prefix ? `${prefix}[${k}]` : k, out);
  } else {
    out.push([prefix, String(value)]);
  }
  return out;
}

function parseBody(body) {
  if (body instanceof FormData) return { kind: 'formdata', data: entriesToObject(body.entries()) };
  if (body instanceof URLSearchParams) return { kind: 'urlencoded', data: entriesToObject(body.entries()) };
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (trimmed.startsWith('{')) {
      try {
        return { kind: 'json', data: JSON.parse(trimmed) };
      } catch (e) {
        return null;
      }
    }
    return { kind: 'urlencoded-string', data: entriesToObject(new URLSearchParams(body).entries()) };
  }
  return null;
}

function toItems(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.items && typeof data.items === 'object') {
    const list = Array.isArray(data.items)
      ? data.items
      : Object.keys(data.items)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => data.items[k]);
    const { items, ...extras } = data;
    return { shape: 'items', items: list.filter(Boolean).map((i) => ({ ...i })), extras };
  }
  if (data.id != null) {
    const { id, quantity, properties, selling_plan, ...extras } = data;
    const item = { id, quantity: quantity ?? 1 };
    if (properties) item.properties = properties;
    if (selling_plan) item.selling_plan = selling_plan;
    return { shape: 'single', items: [item], extras };
  }
  return null;
}

function jsonItem(item) {
  const out = { ...item, id: Number(toId(item.id)) || item.id, quantity: Number(item.quantity) || 1 };
  if (out.properties && !Object.keys(out.properties).length) delete out.properties;
  return out;
}

/* ------------------------------------------------------------------ */
/* Globale API für den Warenkorb-Hook                                  */
/* ------------------------------------------------------------------ */

const instances = new Set();
const api = (window.RBNPersonalisierung = window.RBNPersonalisierung || {});
api.version = '1.0.0';
api.instances = instances;

api.prepareRequest = (body, headers, options = {}) => {
  for (const instance of instances) {
    try {
      const prepared = instance.prepareRequest(body, headers, options);
      if (prepared) return prepared;
    } catch (error) {
      console.error(LOG, 'prepareRequest', error);
    }
  }
  return null;
};

api.afterResponse = (response, prepared) => prepared?.instance?.afterResponse(response, prepared);

/* Antwort von /cart/add.js mit "items" zusätzlich im Einzelartikel-Format liefern (für Theme/Apps, die einen Artikel erwarten) */
api.adaptResponse = async (response) => {
  try {
    if (!response.ok) return response;
    const json = JSON.parse(await response.clone().text());
    if (!Array.isArray(json?.items) || !json.items.length) return response;
    const merged = { ...json.items[0], items: json.items };
    if (json.sections) merged.sections = json.sections;
    return new Response(JSON.stringify(merged), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  } catch (error) {
    return response;
  }
};
api.afterXhr = (xhr, prepared) => prepared?.instance?.afterXhr(xhr, prepared);

api.shouldBlock = (event) => {
  for (const instance of instances) {
    if (instance.shouldBlock(event)) return true;
  }
  return false;
};

/* Fallback, falls snippets/rbn-cart-hook.liquid im Layout fehlt */
function installFallbackHooks() {
  if (window.__rbnCartHook) return;
  window.__rbnCartHook = 'fallback';
  console.warn(LOG, 'rbn-cart-hook fehlt im Layout – Fallback aktiv.');
  const isCartAdd = (url) => {
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(url, location.href).pathname);
    } catch (e) {
      return false;
    }
  };
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input?.href || input?.url;
      if (url && isCartAdd(url) && init?.body != null) {
        const prepared = api.prepareRequest(init.body, init.headers, { transport: 'fetch' });
        if (prepared) {
          return nativeFetch.call(this, input, { ...init, body: prepared.body, headers: prepared.headers }).then((res) => {
            try {
              api.afterResponse(res.clone(), prepared);
            } catch (e) {}
            return prepared.adapt ? api.adaptResponse(res) : res;
          });
        }
      }
    } catch (error) {
      console.error(LOG, error);
    }
    return nativeFetch.apply(this, arguments);
  };
  ['click', 'submit'].forEach((type) =>
    window.addEventListener(
      type,
      (event) => {
        if (api.shouldBlock(event)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    )
  );
}

/* ------------------------------------------------------------------ */
/* Custom Element                                                      */
/* ------------------------------------------------------------------ */

class RbnPersonalizer extends HTMLElement {
  connectedCallback() {
    if (this._initialized) {
      instances.add(this);
      return;
    }
    const root = this.querySelector('[data-rbn-root]');
    const configEl = this.querySelector('[data-rbn-config]');
    if (!root || !configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error(LOG, 'Konfiguration fehlerhaft', error);
      return;
    }
    this._initialized = true;
    this._ready = true;
    if (this.config.fontFamily) FONT = `"${String(this.config.fontFamily).replace(/["']/g, '')}"`;
    if (this.config.propName) PROP_NAME = String(this.config.propName);
    if (this.config.propText) PROP_TEXT = String(this.config.propText);
    this.messages = { ...DEFAULT_MESSAGES };
    for (const [key, value] of Object.entries(this.config.messages || {})) {
      if (typeof value === 'string' && value.trim()) this.messages[key] = value;
    }
    this.root = root;
    this.variantIds = new Set(this.config.variantIds.map(toId));
    this.variantTitles = new Map(this.config.variantIds.map((id, i) => [toId(id), this.config.variantTitles[i]]));
    const labels = this.config.variantLabels || [];
    this.variantLabels = new Map(this.config.variantIds.map((id, i) => [toId(id), labels[i] || 'Set']));
    // Varianten ohne Personalisierung (z. B. Bundle-Option "Nur Hose")
    this.excluded = new Set((this.config.excludedVariantIds || []).map(toId));
    this.kachingInactive = false;
    this.blocked = new Set(
      String(this.config.blockedWords || '')
        .split(/[,\n]/)
        .map(normalizeForCompare)
        .filter(Boolean)
    );

    this.sets = [{ name: '', text: '' }];
    this.active = 0;
    this.kachingUnits = null; // Array der Varianten-IDs pro Set laut Kaching
    this.lastPrepared = null;

    this.els = {
      preview: root.querySelector('[data-preview]'),
      lineName: root.querySelector('[data-line="name"]'),
      lineText: root.querySelector('[data-line="text"]'),
      zoom: root.querySelector('[data-action="toggle-zoom"]'),
      sets: root.querySelector('[data-sets]'),
      inactive: root.querySelector('[data-inactive]'),
      inputName: root.querySelector('[data-field="name"]'),
      inputText: root.querySelector('[data-field="text"]'),
      countName: root.querySelector('[data-count="name"]'),
      countText: root.querySelector('[data-count="text"]'),
      msgName: root.querySelector('[data-msg="name"]'),
      msgText: root.querySelector('[data-msg="text"]'),
      copy: root.querySelector('[data-action="copy-all"]'),
      alert: root.querySelector('[data-alert]'),
    };

    this.bindUi();
    this.bindKaching();
    this.bindForm();

    instances.add(this);
    installFallbackHooks();

    root.hidden = false;
    this.refreshUnits();
    this.render();

    if (document.fonts?.load) {
      Promise.all([document.fonts.load(`32px ${FONT}`, 'ILLYRIAN'), document.fonts.load(`32px ${FONT}`, 'ëçčšž')])
        .catch(() => {})
        .then(() => this.render());
    }
  }

  disconnectedCallback() {
    // Beim Verschieben (z. B. unter das Kaching-Widget) wird das Element kurz entfernt und wieder eingefügt.
    queueMicrotask(() => {
      if (!this.isConnected) instances.delete(this);
    });
  }

  /* ---------------- UI ---------------- */

  bindUi() {
    const { inputName, inputText, zoom, copy, sets } = this.els;

    inputName.addEventListener('input', () => {
      this.sets[this.active].name = inputName.value;
      this.render();
    });
    inputText.addEventListener('input', () => {
      this.sets[this.active].text = inputText.value;
      this.render();
    });

    const mobile = window.matchMedia('(max-width: 749px)');
    const onFocus = () => {
      if (!mobile.matches) return;
      setTimeout(() => this.els.preview.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250);
    };
    inputName.addEventListener('focus', onFocus);
    inputText.addEventListener('focus', onFocus);
    inputName.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        inputText.focus();
      }
    });
    inputText.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        inputText.blur();
      }
    });

    zoom?.addEventListener('click', () => {
      const preview = this.els.preview;
      const full = preview.dataset.mode !== 'full';
      preview.dataset.mode = full ? 'full' : 'zoom';
      zoom.textContent = full ? zoom.dataset.labelZoom : zoom.dataset.labelFull;
    });

    copy.addEventListener('click', () => {
      const source = this.sets[this.active];
      const count = this.unitCount();
      for (let i = 0; i < count; i++) this.sets[i] = { name: source.name, text: source.text };
      this.render();
      this.showInfo(this.t('copied', { count }));
    });

    sets.addEventListener('click', (event) => {
      const button = event.target.closest('[data-set]');
      if (!button) return;
      this.selectSet(Number(button.dataset.set));
    });
    sets.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const count = this.unitCount();
      const next = (this.active + (event.key === 'ArrowRight' ? 1 : -1) + count) % count;
      this.selectSet(next);
      sets.querySelector(`[data-set="${next}"]`)?.focus();
    });
  }

  selectSet(index) {
    this.active = Math.max(0, Math.min(index, this.unitCount() - 1));
    this.render({ syncInputs: true });
  }

  /** Text aus den Block-Einstellungen holen und {{ platzhalter }} ersetzen */
  t(key, vars = {}) {
    const template = (this.messages && this.messages[key]) || DEFAULT_MESSAGES[key] || '';
    return template.replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, (match, a, b) => {
      const name = a || b;
      return name in vars ? String(vars[name]) : match;
    });
  }

  showInfo(text) {
    const { msgText } = this.els;
    msgText.textContent = text;
    msgText.classList.add('is-info');
    clearTimeout(this._infoTimer);
    this._infoTimer = setTimeout(() => {
      if (msgText.classList.contains('is-info')) {
        msgText.textContent = '';
        msgText.classList.remove('is-info');
      }
    }, 4000);
  }

  /* ---------------- Formular / Mengen ---------------- */

  get form() {
    return document.getElementById(this.config.formId) || this.closest('product-form-component')?.querySelector('form') || null;
  }

  bindForm() {
    const update = () => {
      clearTimeout(this._unitTimer);
      this._unitTimer = setTimeout(() => {
        if (this.refreshUnits()) this.render({ syncInputs: true });
      }, 60);
    };
    // Mengenwahl, Variantenwahl, Kaching-Auswahl – alles, was Menge/Variante ändern kann
    document.addEventListener('change', update, true);
    document.addEventListener('click', update, true);
    document.addEventListener('input', (e) => {
      if (e.target instanceof HTMLInputElement && e.target.name === 'quantity') update();
    });
  }

  formQuantity() {
    const form = this.form;
    if (!form) return 1;
    const values = [];
    for (const el of form.elements) {
      if (el.name === 'quantity' && !el.disabled) values.push(Number(el.value));
    }
    const valid = values.filter((v) => Number.isFinite(v) && v > 0);
    return valid.length ? Math.max(...valid) : 1;
  }

  formVariant() {
    const form = this.form;
    const el = form?.elements?.namedItem?.('id');
    const value = el && 'value' in el ? el.value : null;
    return toId(value) || toId(this.config.variantIds[0]);
  }

  /** Liste der Varianten pro Set (Länge = Anzahl Sets). */
  units() {
    if (this.kachingUnits && this.kachingUnits.length) return this.kachingUnits;
    const qty = Math.min(this.formQuantity(), 99);
    const variant = this.formVariant();
    return Array.from({ length: qty }, () => variant);
  }

  unitCount() {
    return Math.max(1, Math.min(this.units().length, this.config.maxSets || 1));
  }

  /** true, wenn nur Artikel ohne Personalisierung gewählt sind (z. B. "Nur Hose"). */
  isInactive() {
    if (this.kachingUnits) return this.kachingInactive;
    return this.excluded.has(this.formVariant());
  }

  refreshUnits() {
    const signature = this.units().join(',');
    const changed = signature !== this._unitSignature;
    this._unitSignature = signature;
    const count = this.unitCount();
    while (this.sets.length < count) this.sets.push({ name: '', text: '' });
    if (this.active >= count) this.active = count - 1;
    return changed;
  }

  /* ---------------- Kaching Bundles ---------------- */

  bindKaching() {
    const onVariants = (event) => {
      const list = event?.detail?.variantIdQuantities;
      if (!Array.isArray(list)) return;
      const units = [];
      for (const entry of list) {
        const id = toId(entry.variantId ?? entry.variant_id ?? entry.id);
        const qty = Math.max(0, Number(entry.quantity) || 0);
        for (let i = 0; i < qty; i++) units.push(id);
      }
      const ours = units.filter((id) => this.variantIds.has(id));
      // Nur Artikel ohne Personalisierung gewählt (z. B. "Nur Hose") → Felder ausblenden
      const onlyExcluded = !ours.length && units.length > 0 && units.every((id) => this.excluded.has(id));
      const wasInactive = this.kachingInactive;
      this.kachingInactive = onlyExcluded;
      this.kachingUnits = ours.length ? ours : units;
      if (this.refreshUnits() || wasInactive !== onlyExcluded) this.render({ syncInputs: true });
    };

    const bind = (el) => {
      if (el.__rbnBound) return;
      el.__rbnBound = true;
      el.addEventListener('variants-changed', onVariants);
      if (this.config.moveAfterKaching) this.placeAfter(el);
    };

    document.querySelectorAll(KACHING_TAG).forEach(bind);
    // Falls das Ereignis bis zum Dokument hochgereicht wird
    document.addEventListener('variants-changed', (e) => {
      if (!(e.target instanceof Element) || !e.target.matches?.(KACHING_TAG) || !e.target.__rbnBound) onVariants(e);
    });

    const observer = new MutationObserver(() => document.querySelectorAll(KACHING_TAG).forEach(bind));
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 20000);
  }

  placeAfter(kachingEl) {
    const container = this.closest('.product-details, product-information, .product-information') || document;
    if (!container.contains(kachingEl)) return;
    if (kachingEl.contains(this)) return;
    const following = kachingEl.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING;
    // Nur verschieben, wenn wir vor dem Widget stehen (sonst steht die Personalisierung schon darunter)
    if (!following) kachingEl.insertAdjacentElement('afterend', this);
  }

  /* ---------------- Prüfen ---------------- */

  check(kind, raw) {
    const isName = kind === 'name';
    const cfg = this.config;
    const value = cleanValue(raw, isName && cfg.nameUppercase);
    const result = { value, ok: true, message: '', fit: 1 };
    if (!value) return result;

    const max = isName ? cfg.nameMax : cfg.textMax;
    if (Array.from(value).length > max) {
      return { ...result, ok: false, message: this.t('max', { max }) };
    }

    if (this.blocked.has(normalizeForCompare(value))) {
      return {
        ...result,
        ok: false,
        message: this.t('placeholder', { value }),
      };
    }

    const bad = Array.from(new Set(Array.from(value).filter((ch) => !ALLOWED_CHAR.test(ch))));
    if (bad.length) {
      return { ...result, ok: false, message: this.t('chars', { chars: bad.join(' ') }) };
    }

    // Druckbereich: Breite bei 1000 px Bildbreite messen (unabhängig von der Bildschirmgrösse)
    const size = (isName ? cfg.nameSize : cfg.textSize) * 10;
    const track = isName ? cfg.nameTrack : cfg.textTrack;
    const allowed = (isName ? cfg.nameMaxWidth : cfg.textMaxWidth) * 10;
    const width = measureWidth(value, size, track);
    const fit = width > allowed ? allowed / width : 1;
    if (fit < cfg.minScale) {
      const approx = Math.max(1, Math.floor(Array.from(value).length * (fit / cfg.minScale)));
      return {
        ...result,
        ok: false,
        fit: cfg.minScale,
        over: true,
        message: this.t('tooLong', { approx }),
      };
    }
    return { ...result, fit };
  }

  validateAll() {
    const count = this.unitCount();
    const sets = [];
    for (let i = 0; i < count; i++) {
      const set = this.sets[i] || { name: '', text: '' };
      const name = this.check('name', set.name);
      const text = this.check('text', set.text);
      if (!name.ok || !text.ok) return { ok: false, index: i, field: !name.ok ? 'name' : 'text', name, text };
      sets.push({ Name: name.value, Wunschtext: text.value });
    }
    return { ok: true, sets };
  }

  /* ---------------- Darstellung ---------------- */

  render({ syncInputs = false } = {}) {
    this.refreshUnits();
    const inactive = this.isInactive();
    this.root.classList.toggle('is-inactive', inactive);
    if (this.els.inactive) this.els.inactive.hidden = !inactive;
    const count = this.unitCount();
    const set = this.sets[this.active];
    const { inputName, inputText, countName, countText, msgName, msgText, lineName, lineText, copy } = this.els;

    if (syncInputs) {
      if (inputName.value !== set.name) inputName.value = set.name;
      if (inputText.value !== set.text) inputText.value = set.text;
    }

    const name = this.check('name', set.name);
    const text = this.check('text', set.text);

    this.paintLine(lineName, name);
    this.paintLine(lineText, text);

    countName.textContent = `${Array.from(set.name).length}/${this.config.nameMax}`;
    countText.textContent = `${Array.from(set.text).length}/${this.config.textMax}`;

    this.setMessage(inputName, msgName, name);
    if (!msgText.classList.contains('is-info') || !text.ok) this.setMessage(inputText, msgText, text);

    this.renderSets(count);
    copy.hidden = count < 2 || !(cleanValue(set.name) || cleanValue(set.text));
  }

  paintLine(el, result) {
    const empty = !result.value;
    el.classList.toggle('is-empty', empty);
    el.classList.toggle('is-invalid', !result.ok);
    el.textContent = empty ? '' : result.value;
    el.style.setProperty('--rbn-fit', String(result.fit || 1));
  }

  setMessage(input, el, result) {
    el.classList.remove('is-info');
    el.textContent = result.ok ? '' : result.message;
    input.setAttribute('aria-invalid', result.ok ? 'false' : 'true');
  }

  renderSets(count) {
    const { sets } = this.els;
    if (count < 2) {
      sets.hidden = true;
      sets.innerHTML = '';
      return;
    }
    const units = this.units();
    const titles = units.slice(0, count).map((id) => this.variantTitles.get(id) || '');
    const kinds = units.slice(0, count).map((id) => this.variantLabels.get(id) || 'Set');
    const showTitles = titles.some(Boolean) && this.variantIds.size > 1;
    const perKind = new Map();
    const html = [];
    for (let i = 0; i < count; i++) {
      const s = this.sets[i] || { name: '', text: '' };
      const filled = !!(cleanValue(s.name) || cleanValue(s.text));
      // Nummer je Art (Set 1, Set 2, Pullover 1 …)
      const n = (perKind.get(kinds[i]) || 0) + 1;
      perKind.set(kinds[i], n);
      const label = `${kinds[i]} ${n}${showTitles && titles[i] ? ` · ${titles[i]}` : ''}`;
      html.push(
        `<button type="button" class="rbn-p__set" role="tab" data-set="${i}" aria-selected="${i === this.active}" tabindex="${
          i === this.active ? 0 : -1
        }">${label.replace(/</g, '&lt;')}${filled ? `<span class="rbn-p__set-check" aria-label="${this.t('personalized').replace(/"/g, '&quot;')}">✓</span>` : ''}</button>`
      );
    }
    const markup = html.join('');
    if (sets.innerHTML !== markup) sets.innerHTML = markup;
    sets.hidden = false;
  }

  showError(result) {
    this.active = result.index;
    this.render({ syncInputs: true });
    const input = result.field === 'name' ? this.els.inputName : this.els.inputText;
    this.root.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setTimeout(() => input.focus({ preventScroll: true }), 300);
  }

  /* ---------------- Warenkorb ---------------- */

  isAddTrigger(event) {
    const form = this.form;
    if (!form) return false;
    if (event.type === 'submit') return event.target === form;
    if (event.type !== 'click') return false;
    const button = event.target instanceof Element ? event.target.closest('button, input[type="submit"]') : null;
    if (!button || button.type !== 'submit' || button.disabled) return false;
    return button.form === form;
  }

  shouldBlock(event) {
    if (!this._ready || !this.isAddTrigger(event)) return false;
    this.refreshUnits();
    if (this.isInactive()) return false;
    const result = this.validateAll();
    if (result.ok) {
      this.els.alert.hidden = true;
      this.armVerification(result.sets);
      return false;
    }
    this.showError(result);
    return true;
  }

  /**
   * Teilt die Warenkorb-Anfrage so auf, dass jedes personalisierte Set eine eigene Zeile mit Menge 1 bekommt.
   * Gibt null zurück, wenn nichts zu tun ist (dann bleibt die Anfrage unverändert).
   */
  prepareRequest(body, headers, options = {}) {
    if (!this._ready) return null;
    const parsed = parseBody(body);
    if (!parsed) return null;
    const request = toItems(parsed.data);
    if (!request || !request.items.some((item) => this.variantIds.has(toId(item.id)))) return null;

    this.refreshUnits();
    if (this.isInactive()) return null;
    const validation = this.validateAll();
    if (!validation.ok) {
      // Sollte durch shouldBlock nie passieren – zur Sicherheit nichts Ungeprüftes senden
      console.warn(LOG, 'Ungültige Personalisierung, Anfrage bleibt unverändert.');
      return null;
    }
    const personal = validation.sets;
    if (!personal.some((p) => p.Name || p.Wunschtext)) return null;

    const plan = this.assignUnits(request.items, personal);
    if (!plan.expected.length) return null;

    const out = this.encode(parsed.kind, request, plan.items, headers, options);
    const prepared = { ...out, instance: this, expected: plan.expected, items: plan.items };
    this.lastPrepared = prepared;
    this._verifiedByHook = true;
    return prepared;
  }

  assignUnits(items, personal) {
    const units = this.units().slice(0, this.unitCount());
    // 1. Durchgang: Einheiten der Anfrage den Sets gleicher Variante zuordnen (Reihenfolge wie im Kaching-Widget)
    const queues = new Map();
    units.forEach((variant, index) => {
      if (!queues.has(variant)) queues.set(variant, []);
      queues.get(variant).push(index);
    });
    const used = new Set();
    const slots = items.map((item) => {
      if (!this.variantIds.has(toId(item.id))) return null;
      const qty = Math.max(1, parseInt(item.quantity ?? 1, 10) || 1);
      const queue = queues.get(toId(item.id)) || [];
      return Array.from({ length: qty }, () => {
        while (queue.length) {
          const index = queue.shift();
          if (!used.has(index)) {
            used.add(index);
            return index;
          }
        }
        return -1;
      });
    });
    // 2. Durchgang: übrige Einheiten der Reihe nach (z. B. wenn die Grösse nicht übereinstimmt)
    const free = units.map((_, i) => i).filter((i) => !used.has(i));
    slots.forEach((slot) => {
      if (!slot) return;
      for (let k = 0; k < slot.length; k++) if (slot[k] === -1 && free.length) slot[k] = free.shift();
    });

    const out = [];
    const expected = [];
    items.forEach((item, itemIndex) => {
      const slot = slots[itemIndex];
      if (!slot) {
        out.push(item);
        return;
      }
      const baseProps = { ...(item.properties || {}) };
      delete baseProps[PROP_NAME];
      delete baseProps[PROP_TEXT];
      let plain = 0;
      for (const index of slot) {
        const p = index >= 0 ? personal[index] : null;
        if (!p || (!p.Name && !p.Wunschtext)) {
          plain++;
          continue;
        }
        const properties = { ...baseProps };
        if (p.Name) properties[PROP_NAME] = p.Name;
        if (p.Wunschtext) properties[PROP_TEXT] = p.Wunschtext;
        out.push({ ...item, quantity: 1, properties });
        expected.push({ set: index + 1, variantId: toId(item.id), ...p });
      }
      if (plain) {
        const rest = { ...item, quantity: plain };
        if (Object.keys(baseProps).length) rest.properties = baseProps;
        else delete rest.properties;
        out.push(rest);
      }
    });
    return { items: out, expected };
  }

  encode(kind, request, items, headers, options) {
    const single = request.shape === 'single' && items.length === 1;
    const nextHeaders = new Headers(headers || {});

    // JSON bleibt JSON; bei fetch wird bei mehreren Zeilen immer JSON gesendet (dokumentierte /cart/add.js-Variante)
    if (kind === 'json' || (options.transport !== 'xhr' && !single)) {
      const payload = single ? { ...request.extras, ...jsonItem(items[0]) } : { ...request.extras, items: items.map(jsonItem) };
      nextHeaders.set('Content-Type', 'application/json');
      // Ursprünglich ein Einzelartikel, jetzt mehrere Zeilen → Antwort für den Aufrufer wieder im Einzelformat anbieten
      const adapt = request.shape === 'single' && !single && options.transport !== 'xhr';
      return { body: JSON.stringify(payload), headers: nextHeaders, adapt };
    }

    const data = single
      ? { ...request.extras, ...items[0], quantity: String(items[0].quantity) }
      : { ...request.extras, items: items.map((i) => ({ ...i, quantity: String(i.quantity) })) };
    const entries = flatten(data, '', []);

    if (kind === 'formdata') {
      const fd = new FormData();
      entries.forEach(([k, v]) => fd.append(k, v));
      nextHeaders.delete('Content-Type'); // Browser setzt Boundary selbst
      return { body: fd, headers: nextHeaders };
    }
    const params = new URLSearchParams();
    entries.forEach(([k, v]) => params.append(k, v));
    return { body: kind === 'urlencoded' ? params : params.toString(), headers: nextHeaders };
  }

  async afterResponse(response, prepared) {
    if (!response.ok) return;
    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      return;
    }
    this.verifyItems(json?.items || (json?.id ? [json] : []), prepared.expected, false);
  }

  afterXhr(xhr, prepared) {
    if (xhr.status < 200 || xhr.status >= 300) return;
    try {
      const json = JSON.parse(xhr.responseText);
      this.verifyItems(json?.items || (json?.id ? [json] : []), prepared.expected, false);
    } catch (e) {}
  }

  /** Nach jedem Klick auf "In den Warenkorb": prüfen, ob die Texte wirklich im Warenkorb gelandet sind. */
  armVerification(sets) {
    const expected = sets
      .map((p, i) => ({ set: i + 1, ...p }))
      .filter((p) => p.Name || p.Wunschtext);
    if (!expected.length) return;
    this._verifiedByHook = false;
    clearTimeout(this._verifyTimer);
    this._verifyTimer = setTimeout(async () => {
      if (this._verifiedByHook) return; // Anfrage lief über den Hook → afterResponse prüft
      try {
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' })).json();
        this.verifyItems(cart.items || [], expected, true);
      } catch (e) {}
    }, 4000);
  }

  verifyItems(items, expected, fromCart) {
    const missing = expected.filter(
      (p) =>
        !items.some(
          (item) =>
            this.variantIds.has(toId(item.variant_id ?? item.id)) &&
            (item.properties?.[PROP_NAME] || '') === (p.Name || '') &&
            (item.properties?.[PROP_TEXT] || '') === (p.Wunschtext || '')
        )
    );
    if (!missing.length) {
      this.els.alert.hidden = true;
      return true;
    }
    console.error(LOG, 'Personalisierung fehlt im Warenkorb', { missing, items, fromCart });
    this.els.alert.textContent = this.t('cartMissing');
    this.els.alert.hidden = false;
    return false;
  }
}

if (!customElements.get('rbn-personalizer')) customElements.define('rbn-personalizer', RbnPersonalizer);
